import React, { useEffect, useState } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { authAPI } from '../../helpers/api_service';
import { useThemeMode } from '../../ThemeContext';
import './DeviceConflictPopup.css';

const DeviceConflictPopup = ({ 
  show, 
  onHide, 
  onContinue, 
  activeDeviceSessions, 
  loginData,
  isLoading, 
  setIsLoading,
  setError 
}) => {
  const { mode } = useThemeMode();
  const [loggingOutSessions, setLoggingOutSessions] = useState(new Set());
  const [sessions, setSessions] = useState([]);

  // Sync local sessions state when the modal opens or the source list changes
  useEffect(() => {
    if (Array.isArray(activeDeviceSessions)) {
      setSessions(activeDeviceSessions);
    } else {
      setSessions([]);
    }
  }, [activeDeviceSessions, show]);

  const handleLogoutDevice = async (sessionId) => {
    setIsLoading(true);
    setLoggingOutSessions(prev => new Set(prev).add(sessionId));
    
    try {
      // Save the token from login response before calling expire API
      if (loginData && loginData.token) {
        localStorage.setItem('authToken', loginData.token);
      }
      
      const response = await authAPI.expireDevice(sessionId);
      if (response.status === 'success') {
        // Remove the logged out session from the local list and proceed
        setSessions(prev => prev.filter(session => session.session_id !== sessionId));
        // As per flow, proceed after logging out any one device
        onContinue();
      } else {
        setError(response.message || 'Failed to logout from device. Please try again.');
      }
    } catch (err) {
      console.error('Error expiring device:', err);
      setError('Network error. Please check your connection and try again.');
    } finally {
      setIsLoading(false);
      setLoggingOutSessions(prev => {
        const newSet = new Set(prev);
        newSet.delete(sessionId);
        return newSet;
      });
    }
  };

  const handleCancel = async () => {
    // Call the regular logout API when canceling
    setIsLoading(true);
    try {
      // Save the token from login response before calling logout API
      if (loginData && loginData.token) {
        localStorage.setItem('authToken', loginData.token);
      }
      
      const response = await authAPI.logout();
      if (response.status === 'success') {
        onHide();
      } else {
        setError(response.message || 'Failed to logout. Please try again.');
      }
    } catch (err) {
      console.error('Error during logout:', err);
      setError('Network error. Please check your connection and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const formatDeviceInfo = (deviceInfo) => {
    if (!deviceInfo) return { device_type: 'Unknown', browser_name: 'Unknown' };
    
    try {
      const parsed = JSON.parse(deviceInfo);
      return {
        device_type: parsed.device_type || 'Unknown',
        browser_name: parsed.browser_name || 'Unknown',
        os_name: parsed.os_name || 'Unknown',
        os_version: parsed.os_version || ''
      };
    } catch (e) {
      return { device_type: 'Unknown', browser_name: 'Unknown' };
    }
  };

  const getUserAvatar = (username) => {
    if (!username) return '?';
    return username.charAt(0).toUpperCase();
  };

  const getDeviceIcon = (deviceType) => {
    // Debug log to see what device type is being passed
    console.log('Device type received:', deviceType);
    
    if (!deviceType) return 'fas fa-desktop';
    
    const type = deviceType.toLowerCase().trim();
    
    switch (type) {
      case 'web':
      case 'browser':
      case 'desktop':
        return 'fas fa-globe';
      case 'mobile':
      case 'phone':
      case 'smartphone':
        return 'fas fa-mobile-alt';
      case 'tablet':
      case 'ipad':
        return 'fas fa-tablet-alt';
      case 'laptop':
        return 'fas fa-laptop';
      default:
        return 'fas fa-desktop';
    }
  };

  const getDeviceIconText = (deviceType) => {
    if (!deviceType) return '💻';
    
    const type = deviceType.toLowerCase().trim();
    
    switch (type) {
      case 'web':
      case 'browser':
      case 'desktop':
        return '🌐';
      case 'mobile':
      case 'phone':
      case 'smartphone':
        return '📱';
      case 'tablet':
      case 'ipad':
        return '📱';
      case 'laptop':
        return '💻';
      default:
        return '💻';
    }
  };

  const formatLoginTime = (loginTime) => {
    if (!loginTime) return 'Unknown time';
    
    try {
      const date = new Date(loginTime);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const seconds = String(date.getSeconds()).padStart(2, '0');
      
      return `${day}-${month}-${year}, ${hours}:${minutes}:${seconds}`;
    } catch (e) {
      return loginTime;
    }
  };

  return (
    <Modal 
      show={show} 
      onHide={onHide} 
      centered 
      backdrop="static" 
      keyboard={false}
      className={`device-conflict-modal ${mode === 'dark' ? 'dark-mode' : 'light-mode'}`}
    >
      <Modal.Header 
        closeButton 
        className={`device-conflict-header ${mode === 'dark' ? 'dark-mode' : 'light-mode'}`}
        onHide={handleCancel}
      >
        <Modal.Title>
          <div className="device-conflict-title">
            <h4>Device Limit Reached</h4>
          </div>
        </Modal.Title>
      </Modal.Header>
      
      <Modal.Body className={`device-conflict-body ${mode === 'dark' ? 'dark-mode' : 'light-mode'}`}>
        <div className="security-message">
          <p className="security-description">
            Device limit reached. Please logout from any of this active session to continue.
          </p>
        </div>
        
        <div className="active-sessions-list">
          {sessions && sessions.map((session, index) => {
            const deviceInfo = formatDeviceInfo(session.device_info);
            const isLoggingOut = loggingOutSessions.has(session.session_id);
            
            return (
              <div key={session.session_id} className={`device-session-card ${mode === 'dark' ? 'dark-mode' : 'light-mode'}`}>
                <div className="session-card-content">
                  {/* User Avatar */}
                  <div className={`user-avatar ${mode === 'dark' ? 'dark-mode' : 'light-mode'}`}>
                    <span className="user-avatar-icon">{getUserAvatar(session.username)}</span>
                  </div>
                  
                  {/* Session Info */}
                  <div className="session-info">
                    <h5 className="username">{session.username}</h5>
                    <div className="device-info">
                      <div className="device-details">
                        <div className="device-icon-container">
                          {(() => {
                            // Check if Font Awesome is loaded by testing if the icon class exists
                            const iconClass = getDeviceIcon(session.device_type);
                            const isFontAwesomeLoaded = document.querySelector('link[href*="font-awesome"]') || 
                                                      document.querySelector('link[href*="fontawesome"]') ||
                                                      window.FontAwesome;
                            
                            if (isFontAwesomeLoaded) {
                              return <i className={`device-icon ${iconClass}`}></i>;
                            } else {
                              return <span className="device-icon-fallback">{getDeviceIconText(session.device_type)}</span>;
                            }
                          })()}
                        </div>
                        <span className="device-text">
                          {deviceInfo.browser_name}
                        </span>
                      </div>
                      <div className="login-time">
                        {formatLoginTime(session.login_time)}
                      </div>
                    </div>
                  </div>
                  
                  {/* Logout Button */}
                  <div className="session-actions">
                    <Button 
                      className={`logout-device-btn ${mode === 'dark' ? 'dark-mode' : 'light-mode'}`}
                      onClick={() => handleLogoutDevice(session.session_id)}
                      disabled={isLoading || isLoggingOut}
                    >
                      {isLoggingOut ? (
                        <>
                          <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                          <span>Logging out...</span>
                        </>
                      ) : (
                        <>
                          <i className="fas fa-sign-out-alt logout-icon"></i>
                          <span>Logout</span>
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </Modal.Body>
      
    </Modal>
  );
};

export default DeviceConflictPopup;
